import {Component, EventEmitter, Input, NgZone, OnChanges, OnInit, Output} from '@angular/core';
import {NgbDateStruct} from '@ng-bootstrap/ng-bootstrap';
import {UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators} from '@angular/forms';
import {DataService} from '../../../Services/data.service';
import {BsToastService} from '../../../Services/bs-toast-service';
import {DatePipe} from '@angular/common';
import {GeocodingService} from '../../../Services/geocoding.service';

@Component({
  selector: 'app-time-entry-form',
  templateUrl: './time-entry-form.component.html',
  styleUrls: ['./time-entry-form.component.css'],
  standalone: false
})
export class TimeEntryFormComponent implements OnInit, OnChanges {
  @Input() timesheetId: string;
  @Input() employeeId: string | null = null;
  @Input() entryId: string | null = null;
  @Input() periodStart: NgbDateStruct | null = null;
  @Input() periodEnd: NgbDateStruct | null = null;
  @Output() entrySaved = new EventEmitter<void>();
  @Output() modalClose = new EventEmitter<void>();

  TimeEntryForm: UntypedFormGroup;
  submitted = false;
  buttonsDisabled = false;

  entryTypeOptions = ['regular', 'overtime', 'pto', 'sick', 'holiday'];

  item: any = {};
  projects: any[] = [];
  tasks: any[] = [];
  projectTimekeepingEnabled = false;
  projectUserId: string | null = null;
  resolvedAddress: string | null = null;

  constructor(
    private tableDataService: DataService,
    private notification: BsToastService,
    private datePipe: DatePipe,
    private formBuilder: UntypedFormBuilder,
    private geocodingService: GeocodingService,
    private ngZone: NgZone,
  ) {
  }

  ngOnInit() {
    this.formInit();
    this.loadEmployeeFlags();
    this.loadEntry();
  }

  ngOnChanges() {
    if (this.TimeEntryForm) {
      this.loadEntry();
    }
    // Reload employee flags whenever employeeId changes
    this.loadEmployeeFlags();
  }

  formInit() {
    this.TimeEntryForm = this.formBuilder.group({
      item_entry_date: new UntypedFormControl('', Validators.required),
      item_clock_in: new UntypedFormControl(''),
      item_clock_out: new UntypedFormControl(''),
      item_hours: new UntypedFormControl(''),
      item_entry_type: new UntypedFormControl('regular', Validators.required),
      item_notes: new UntypedFormControl('', Validators.required),
    });

    this.TimeEntryForm.controls.item_clock_in.valueChanges.subscribe(() => this.calcHours());
    this.TimeEntryForm.controls.item_clock_out.valueChanges.subscribe(() => this.calcHours());
  }

  loadEntry() {
    if (!this.entryId) {
      this.item = {};
      if (this.TimeEntryForm) {
        const today = this.datePipe.transform(new Date(), 'MM/dd/yyyy');
        this.TimeEntryForm.reset({item_entry_date: today, item_entry_type: 'regular', item_clock_in: '08:00', item_clock_out: '17:00'});
        this.calcHours();
      }
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          async (pos) => {
            const address = await this.geocodingService.getAddress(pos.coords.latitude, pos.coords.longitude);
            this.ngZone.run(() => {
              this.item = {...this.item, gps_lat: pos.coords.latitude, gps_lon: pos.coords.longitude};
              console.log('address: ' + address);
              this.resolvedAddress = address;
            });
          },
          () => { /* user denied or unavailable — leave null */
          }
        );
      }
      return;
    }
    this.tableDataService.getData('pr_time_entry', this.entryId).subscribe({
      next: async (result: any) => {
        console.log('timesheet entry', result);
        this.item = result;
        this.item.entry_date = this.datePipe.transform(this.item.entry_date, 'MM/dd/yyyy');
        // Preserve project_name / task_name returned by the API for display on billed entries
        this.item._project_name = result.project_name ?? null;
        this.item._task_name = result.task_name ?? null;
        this.setValues();
        this.calcHours();
        // If entry already has a project, populate tasks without clearing task_id
        if (this.item.project_id) {
          this.loadTasksForProject(this.item.project_id);
        }
        // Resolve address if coordinates are present
        if (this.item.gps_lat != null && this.item.gps_lon != null) {
          this.resolvedAddress = await this.geocodingService.getAddress(this.item.gps_lat, this.item.gps_lon);
        }
      },
      error: (err) => {
        console.error(err);
        this.notification.showErrorToast($localize`:Error loading entry@@errorLoadingEntry:Error loading entry`);
      },
    });
  }

  loadEmployeeFlags() {
    if (!this.employeeId) {
      this.projectTimekeepingEnabled = false;
      this.projectUserId = null;
      this.projects = [];
      this.tasks = [];
      return;
    }
    this.tableDataService.getData('employee', this.employeeId).subscribe({
      next: (emp: any) => {
        this.projectTimekeepingEnabled = emp?.project_timekeeping_enabled === true;
        this.projectUserId = emp?.project_user_id ?? null;
        if (this.projectTimekeepingEnabled && this.projectUserId) {
          this.loadProjects();
        } else {
          this.projects = [];
          this.tasks = [];
        }
      },
      error: () => {
        this.projectTimekeepingEnabled = false;
        this.projectUserId = null;
        this.projects = [];
        this.tasks = [];
      }
    });
  }

  loadProjects() {
    if (!this.projectUserId) {
      return;
    }
    const url = `project/assigned/${this.projectUserId}`;
    this.tableDataService.getData('project/assigned', this.projectUserId).subscribe({
      next: (result: any) => {
        this.projects = Array.isArray(result) ? result : (result?.items ?? result?.Items ?? []);
        // If the entry was already loaded and has a project, populate tasks now that projects are available
        if (this.item?.project_id) {
          this.loadTasksForProject(this.item.project_id);
        }
      },
      error: (err: any) => {
        console.error('[TimeEntry] loadProjects — error:', err);
        this.projects = [];
      }
    });
  }

  getProjectName(projectId: string | null): string {
    if (!projectId) {
      return '—';
    }
    if (this.item?._project_name) {
      return this.item._project_name;
    }
    const p = this.projects.find((x: any) => x.project_id === projectId);
    return p ? `${p.project_number} — ${p.project_name}` : projectId;
  }

  getTaskName(taskId: string | null): string {
    if (!taskId) {
      return '—';
    }
    if (this.item?._task_name) {
      return this.item._task_name;
    }
    const t = this.tasks.find((x: any) => x.task_id === taskId);
    return t ? t.task_name : taskId;
  }

  onProjectChange() {
    this.item.task_id = null;
    this.tasks = [];
    if (!this.item.project_id) {
      return;
    }
    const selected = this.projects.find((p: any) => p.project_id === this.item.project_id);
    this.tasks = selected?.tasks ?? [];
  }

  loadTasksForProject(projectId: any) {
    const selected = this.projects.find((p: any) => p.project_id === projectId);
    this.tasks = selected?.tasks ?? [];
  }

  get f() {
    return this.TimeEntryForm.controls;
  }

  isoToTime(iso: string | null): string {
    if (!iso) {
      return '';
    }
    try {
      const d = new Date(iso);
      const hh = d.getHours().toString().padStart(2, '0');
      const mm = d.getMinutes().toString().padStart(2, '0');
      return hh + ':' + mm;
    } catch {
      return '';
    }
  }

  setValues() {
    this.TimeEntryForm.controls.item_entry_date.setValue(this.item.entry_date);
    this.TimeEntryForm.controls.item_clock_in.setValue(this.isoToTime(this.item.clock_in));
    this.TimeEntryForm.controls.item_clock_out.setValue(this.isoToTime(this.item.clock_out));
    this.TimeEntryForm.controls.item_hours.setValue(this.item.hours);
    this.TimeEntryForm.controls.item_entry_type.setValue(this.item.entry_type ?? 'regular');
    this.TimeEntryForm.controls.item_notes.setValue(this.item.notes);
  }

  calcHours() {
    const clockIn = this.TimeEntryForm.controls.item_clock_in.value;
    const clockOut = this.TimeEntryForm.controls.item_clock_out.value;
    if (clockIn && clockOut) {
      try {
        const inParts = clockIn.split(':');
        const outParts = clockOut.split(':');
        const inMinutes = parseInt(inParts[0], 10) * 60 + parseInt(inParts[1], 10);
        const outMinutes = parseInt(outParts[0], 10) * 60 + parseInt(outParts[1], 10);
        const diff = outMinutes - inMinutes;
        if (diff > 0) {
          this.TimeEntryForm.controls.item_hours.setValue((diff / 60).toFixed(2), {emitEvent: false});
        }
      } catch {
        // ignore parse errors
      }
    }
  }

  timeToIso(timeStr: string, refDate: string): string | null {
    if (!timeStr) {
      return null;
    }
    try {
      const base = refDate ? new Date(refDate) : new Date();
      const [hh, mm] = timeStr.split(':').map(Number);
      base.setHours(hh, mm, 0, 0);
      return base.toISOString();
    } catch {
      return null;
    }
  }

  setupValuesForSave() {
    this.item.timesheet_id = this.timesheetId;
    const entryDateVal = this.TimeEntryForm.controls.item_entry_date.value;
    this.item.entry_date = new Date(entryDateVal).toISOString();
    this.item.clock_in = this.timeToIso(this.TimeEntryForm.controls.item_clock_in.value, entryDateVal);
    this.item.clock_out = this.timeToIso(this.TimeEntryForm.controls.item_clock_out.value, entryDateVal);
    this.item.hours = this.TimeEntryForm.controls.item_hours.value || null;
    this.item.entry_type = this.TimeEntryForm.controls.item_entry_type.value;
    this.item.notes = this.TimeEntryForm.controls.item_notes.value;
    // project/task — keep whatever is already on this.item (bound via ngModel)
    if (!this.item.project_id) {
      this.item.project_id = null;
    }
    if (!this.item.task_id) {
      this.item.task_id = null;
    }
    this.item.billed = this.item.billed ?? false;
    if (!this.item.project_id) {
      this.item.project_nonbillable = false;
    } else {
      this.item.project_nonbillable = this.item.project_nonbillable ?? false;
    }
  }

  onSave() {
    this.submitted = true;
    this.buttonsDisabled = true;

    if (this.TimeEntryForm.invalid) {
      this.notification.showWarningToast($localize`:Form Errors!@@formerrors:Form Errors!`);
      this.buttonsDisabled = false;
      return;
    }

    // Validate entry date is within pay period bounds
    if (this.periodStart && this.periodEnd) {
      const entryDateVal = this.TimeEntryForm.controls.item_entry_date.value;
      const entryDate = new Date(entryDateVal);
      const start = new Date(this.periodStart.year, this.periodStart.month - 1, this.periodStart.day);
      const end = new Date(this.periodEnd.year, this.periodEnd.month - 1, this.periodEnd.day);
      if (entryDate < start || entryDate > end) {
        this.notification.showWarningToast(
          $localize`:EntryDateOutOfRange@@entryDateOutOfRange:Entry date must be within the pay period.`
        );
        this.buttonsDisabled = false;
        return;
      }
    }

    this.setupValuesForSave();

    if (!this.item.id) {
      this.tableDataService.post('pr_time_entry', this.item).subscribe({
        next: () => {
          this.notification.showSuccessToast($localize`:Saved@@saved:Saved`);
          this.entrySaved.emit();
        },
        error: (err) => {
          this.buttonsDisabled = false;
          console.error(err);
        },
        complete: () => {
          this.buttonsDisabled = false;
          this.submitted = false;
        },
      });
    } else {
      this.tableDataService.patch('pr_time_entry', this.item.id, this.item)
        .then(() => {
          this.notification.showSuccessToast($localize`:Saved@@saved:Saved`);
          this.entrySaved.emit();
        })
        .catch((err) => {
          this.buttonsDisabled = false;
          console.error(err);
        })
        .finally(() => {
          this.buttonsDisabled = false;
          this.submitted = false;
        });
    }
  }

  onClose() {
    this.modalClose.emit();
  }
}
